import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IWorkflowBlueprint,
  IWorkflowRun,
  WorkflowRunStatus
} from '@care-portals/api-interfaces';
import { WorkflowRunsStatuses } from '@care-portals/data';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { LiteJsonEditorComponent } from '../json-editor/json-editor.component';
import {
  buildWorkflowRunErrorPresentation,
  buildWorkflowSnapshotPresentation,
  EMPTY_ERROR_PRESENTATION,
  WorkflowRunErrorPresentation,
  WorkflowSnapshotPresentation
} from './workflow-run-details.utils';

/**
 * Shared "Workflow Run Details" view (overview, error, payload, snapshot) used
 * by both CRM and EMR so the same run inspector renders in either app. Actions
 * are opt-in via the `show*` inputs and surfaced as outputs — the host app owns
 * the data load and the actual API calls.
 */
@Component({
  selector: 'workflow-run-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzCardModule,
    NzTagModule,
    NzButtonModule,
    NzIconModule,
    NzPopconfirmModule,
    NzDescriptionsModule,
    NzCollapseModule,
    NzTimelineModule,
    LiteJsonEditorComponent
  ],
  templateUrl: './workflow-run-details-view.component.html',
  styleUrls: ['./workflow-run-details-view.component.less']
})
export class WorkflowRunDetailsViewComponent implements OnChanges {
  /** The full workflow run to display. */
  @Input() run: IWorkflowRun | null = null;
  @Input() inlineHeaderActions = false;

  @Input() showUpdateFromBlueprint = false;
  @Input() showMarkResolved = false;
  @Input() showEditWorkflow = false;
  @Input() showRerun = false;
  /** Show a spinner on the Rerun button while the host processes the rerun. */
  @Input() rerunLoading = false;
  /**
   * Show the raw JSON sections (payload body + raw snapshot). CRM keeps these;
   * EMR sets this to false so clinical users don't see raw JSON.
   */
  @Input() showRawJson = true;

  @Output() updateFromBlueprint = new EventEmitter<void>();
  @Output() markResolved = new EventEmitter<void>();
  @Output() editWorkflow = new EventEmitter<void>();
  @Output() rerun = new EventEmitter<void>();

  readonly statuses = WorkflowRunsStatuses;

  readonly iconMap: Record<WorkflowRunStatus, string> = {
    running: 'loading',
    completed: 'check-circle',
    failed: 'close-circle',
    idle: 'pause-circle'
  };

  /** Parsed, human-friendly error rows (derived from `run.error`). */
  errorPresentation: WorkflowRunErrorPresentation = EMPTY_ERROR_PRESENTATION;

  /** Execution timeline + metadata derived from `run.snapshot`. */
  snapshotPresentation: WorkflowSnapshotPresentation =
    buildWorkflowSnapshotPresentation(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['run']) {
      this.errorPresentation = this.run?.error
        ? buildWorkflowRunErrorPresentation(this.run.error)
        : EMPTY_ERROR_PRESENTATION;

      this.snapshotPresentation = buildWorkflowSnapshotPresentation(
        this.run?.snapshot as IWorkflowBlueprint,
        this.run?.status
      );
    }
  }
}
